import { FunctionComponent, useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useSelector } from '@cbridge-utils/react-redux';
import { ToggleExpandButton } from '@cbridge-tools/button';
import { NavigationListItemButton, NavigationItemVerticalPadding, NavigationItemHorizontalPadding } from '@cbridge-tools/navigation-item';
import { isMatchedPathnameSelector } from '@cbridge-packages/routing';
import { LinkWithSearchParams } from '../LinkWithSearchParams/LinkWithSearchParams';
import { INavigationBlockRecursiveItemProps } from './NavigationBlockRecursiveItem.interface';

export const NavigationBlockRecursiveItem: FunctionComponent<INavigationBlockRecursiveItemProps> = ({ item, depth = 0 }) => {
  const { label, href, items = [], isExpandedByDefault = false } = item;
  const [expanded, setExpanded] = useState<boolean>(isExpandedByDefault);

  const isSelected = useSelector(isMatchedPathnameSelector, { pattern: href });

  const hasChildren = items.length > 0;

  // A group row without its own route (e.g. a coverage server) only expands/collapses —
  // rendering it as a link would navigate to the section root.
  const buttonProps = href
    ? { selected: isSelected, component: LinkWithSearchParams, to: href }
    : { onClick: () => setExpanded((value) => !value) };

  return (
    <>
      <ListItem
        sx={{
          p: 0,
          overflowX: 'hidden',
        }}
      >
        <NavigationListItemButton
          py={NavigationItemVerticalPadding.sm}
          px={NavigationItemHorizontalPadding.none}
          {...buttonProps}
        >
          <Box
            sx={{
              width: 23,
              height: 23,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: depth,
            }}
          >
            {hasChildren && (
              <ToggleExpandButton
                size={'small'}
                isExpanded={expanded}
                onChange={setExpanded}
                sx={{ p: 0 }}
              />
            )}
          </Box>
          <ListItemText
            primary={label}
            slotProps={{
              primary: {
                noWrap: true,
              },
            }}
          />
        </NavigationListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={expanded} timeout={'auto'} unmountOnExit={true}>
          <Box>
            {items.map((subItem, index) => (
              <NavigationBlockRecursiveItem
                key={subItem.href || index}
                item={subItem}
                depth={depth + 1}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};
